import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Package, Edit, Trash2, Plus, Upload, ChevronDown, ChevronUp, GripVertical, ExternalLink, Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useBundles, useRetailers, useKitTypes, useBundleVersions } from '@/hooks/useBundles';
import * as bundlesData from '@/data/bundles';
import type { BundleWithRelations, BundleItem, Retailer, BundleKitType, BundleVersion } from '@/contracts/bundles';
import { supabase } from '@/integrations/supabase/client';

const AdminBundles = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [filters, setFilters] = useState({ is_published: undefined, limit: 100, offset: 0 });
  const { data: bundles, isLoading: bundlesLoading, refetch } = useBundles(filters);
  const { data: retailers } = useRetailers();
  const { data: kitTypes } = useKitTypes();
  const { data: versions } = useBundleVersions();

  const [editingBundle, setEditingBundle] = useState<BundleWithRelations | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkImportJson, setBulkImportJson] = useState('');
  const [importStats, setImportStats] = useState<{ success: number; errors: string[] } | null>(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, authLoading, navigate]);

  if (authLoading || !isAdmin) {
    return null;
  }

  const handleTogglePublished = async (bundleId: string, currentPublished: boolean) => {
    try {
      await bundlesData.toggleBundlePublished(bundleId, currentPublished);

      toast({
        title: 'Bundle updated',
        description: `Bundle ${!currentPublished ? 'published' : 'unpublished'} successfully`,
      });
      refetch();
    } catch (error) {
      console.error('Error toggling published:', error);
      toast({
        title: 'Error',
        description: 'Failed to update bundle',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteBundle = async (bundleId: string) => {
    try {
      await bundlesData.deleteBundle(bundleId);
      toast({
        title: 'Bundle deleted',
        description: 'Bundle and all items removed successfully',
      });
      refetch();
    } catch (error) {
      console.error('Error deleting bundle:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete bundle',
        variant: 'destructive',
      });
    }
  };

  const handleBulkImport = async () => {
    try {
      const data = JSON.parse(bulkImportJson);
      
      const { data: result, error } = await supabase.functions.invoke('seed-bundles', {
        body: { bundles: Array.isArray(data) ? data : [data], overwrite: false },
      });

      if (error) throw error;

      setImportStats({
        success: result.stats.bundlesCreated + result.stats.bundlesUpdated,
        errors: result.stats.errors || [],
      });

      toast({
        title: 'Import complete',
        description: `Successfully imported ${result.stats.bundlesCreated} bundles`,
      });
      
      refetch();
      setBulkImportJson('');
    } catch (error: any) {
      console.error('Bulk import error:', error);
      toast({
        title: 'Import failed',
        description: error.message || 'Failed to import bundles',
        variant: 'destructive',
      });
    }
  };

  const handleCreateBundle = async (formData: {
    retailer_id: string;
    kit_type_id: string;
    version_id: string;
    summary: string;
  }) => {
    try {
      const retailer = retailers?.find(r => r.id === formData.retailer_id);
      const kitType = kitTypes?.find(k => k.id === formData.kit_type_id);
      const version = versions?.find(v => v.id === formData.version_id);

      const slug = `${retailer?.slug}-${kitType?.slug}-${version?.slug}`;

      await bundlesData.createBundle({
        slug,
        retailer_id: formData.retailer_id,
        kit_type_id: formData.kit_type_id,
        version_id: formData.version_id,
        summary: formData.summary,
      });

      toast({
        title: 'Bundle created',
        description: 'New bundle created successfully',
      });
      
      setIsCreateModalOpen(false);
      refetch();
    } catch (error: any) {
      console.error('Error creating bundle:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create bundle',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2">Gear Bundles Management</h1>
              <p className="text-muted-foreground">
                Manage bundle kits, items, and bulk imports
              </p>
            </div>
            <div className="flex gap-2">
              <Link to="/admin/retailers">
                <Button variant="outline">
                  <Store className="mr-2 h-4 w-4" />
                  Manage Retailers
                </Button>
              </Link>
              <Dialog open={isBulkImportOpen} onOpenChange={setIsBulkImportOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Upload className="mr-2 h-4 w-4" />
                    Bulk Import
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Bulk Import Bundles</DialogTitle>
                    <DialogDescription>
                      Paste JSON array of bundles to import. Each bundle should include retailer, kit_name, version, summary, and items array.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Textarea
                      placeholder={`[\n  {\n    "retailer": "Amazon",\n    "kit_name": "Open House",\n    "version": "Pro",\n    "summary": "Professional open house essentials",\n    "items": [\n      {\n        "name": "Ring Light",\n        "description": "12-inch LED ring light",\n        "search_query": "ring light for real estate",\n        "price_range": "$40-80",\n        "category": "Photography"\n      }\n    ]\n  }\n]`}
                      value={bulkImportJson}
                      onChange={(e) => setBulkImportJson(e.target.value)}
                      className="font-mono text-sm min-h-[400px]"
                    />
                    {importStats && (
                      <div className="space-y-2">
                        <div className="text-sm text-green-600 dark:text-green-400">
                          ✓ Successfully imported {importStats.success} bundles
                        </div>
                        {importStats.errors.length > 0 && (
                          <div className="space-y-1">
                            <div className="text-sm text-destructive">Errors:</div>
                            {importStats.errors.map((err, i) => (
                              <div key={i} className="text-xs text-muted-foreground">{err}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setBulkImportJson('')}>Clear</Button>
                    <Button onClick={handleBulkImport} disabled={!bulkImportJson.trim()}>
                      Import Bundles
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <CreateBundleModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                retailers={retailers || []}
                kitTypes={kitTypes || []}
                versions={versions || []}
                onSubmit={handleCreateBundle}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Total Bundles</CardDescription>
                <CardTitle className="text-3xl">{bundles?.length || 0}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Published</CardDescription>
                <CardTitle className="text-3xl">
                  {bundles?.filter(b => b.is_published).length || 0}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Total Items</CardDescription>
                <CardTitle className="text-3xl">
                  {bundles?.reduce((sum, b) => sum + b.item_count, 0) || 0}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Total Clicks</CardDescription>
                <CardTitle className="text-3xl">
                  {bundles?.reduce((sum, b) => sum + b.clicks_count, 0) || 0}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Bundles Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Bundles</CardTitle>
              <CardDescription>View and manage all gear bundle kits</CardDescription>
            </CardHeader>
            <CardContent>
              {bundlesLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading bundles...</div>
              ) : bundles && bundles.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slug</TableHead>
                      <TableHead>Retailer</TableHead>
                      <TableHead>Kit Type</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Price Range</TableHead>
                      <TableHead>Views</TableHead>
                      <TableHead>Clicks</TableHead>
                      <TableHead>Published</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bundles.map((bundle) => (
                      <TableRow key={bundle.id}>
                        <TableCell className="font-mono text-xs">{bundle.slug}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {bundle.retailer.logo_url && (
                              <img src={bundle.retailer.logo_url} alt="" className="h-6 w-6 object-contain" />
                            )}
                            <span className="text-sm">{bundle.retailer.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{bundle.kit_type.name}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            style={{ backgroundColor: bundle.version.badge_color || undefined }}
                          >
                            {bundle.version.name}
                          </Badge>
                        </TableCell>
                        <TableCell>{bundle.item_count}</TableCell>
                        <TableCell className="text-sm">
                          {bundle.total_price_min && bundle.total_price_max
                            ? `$${bundle.total_price_min}-${bundle.total_price_max}`
                            : '-'}
                        </TableCell>
                        <TableCell>{bundle.views_count}</TableCell>
                        <TableCell>{bundle.clicks_count}</TableCell>
                        <TableCell>
                          <Switch
                            checked={bundle.is_published}
                            onCheckedChange={() => handleTogglePublished(bundle.id, bundle.is_published)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingBundle(bundle);
                                setIsEditModalOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Bundle?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete the bundle and all {bundle.item_count} items. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteBundle(bundle.id)}>
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No bundles yet</h3>
                  <p className="text-muted-foreground mb-4">Create your first bundle or use bulk import</p>
                  <Button onClick={() => setIsCreateModalOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Bundle
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />

      {/* Edit Bundle Modal */}
      {editingBundle && (
        <EditBundleModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          bundle={editingBundle}
          onSave={() => {
            refetch();
            setIsEditModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

// Create Bundle Modal Component
const CreateBundleModal = ({
  open,
  onOpenChange,
  retailers,
  kitTypes,
  versions,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  retailers: Retailer[];
  kitTypes: BundleKitType[];
  versions: BundleVersion[];
  onSubmit: (data: any) => void;
}) => {
  const [formData, setFormData] = useState({
    retailer_id: '',
    kit_type_id: '',
    version_id: '',
    summary: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ retailer_id: '', kit_type_id: '', version_id: '', summary: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Create Bundle
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Bundle</DialogTitle>
          <DialogDescription>
            Create a new bundle kit. You can add items after creating the bundle.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="retailer">Retailer</Label>
            <Select value={formData.retailer_id} onValueChange={(value) => setFormData(prev => ({ ...prev, retailer_id: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select retailer" />
              </SelectTrigger>
              <SelectContent>
                {retailers.map((retailer) => (
                  <SelectItem key={retailer.id} value={retailer.id}>
                    {retailer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="kit_type">Kit Type</Label>
            <Select value={formData.kit_type_id} onValueChange={(value) => setFormData(prev => ({ ...prev, kit_type_id: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select kit type" />
              </SelectTrigger>
              <SelectContent>
                {kitTypes.map((kitType) => (
                  <SelectItem key={kitType.id} value={kitType.id}>
                    {kitType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="version">Version</Label>
            <Select value={formData.version_id} onValueChange={(value) => setFormData(prev => ({ ...prev, version_id: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map((version) => (
                  <SelectItem key={version.id} value={version.id}>
                    {version.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <Textarea
              id="summary"
              placeholder="Brief description of this bundle..."
              value={formData.summary}
              onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formData.retailer_id || !formData.kit_type_id || !formData.version_id || !formData.summary}>
              Create Bundle
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Bundle Modal Component
const EditBundleModal = ({
  open,
  onOpenChange,
  bundle,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bundle: BundleWithRelations;
  onSave: () => void;
}) => {
  const { toast } = useToast();
  const [summary, setSummary] = useState(bundle.summary);
  const [priceMin, setPriceMin] = useState(bundle.total_price_min || 0);
  const [priceMax, setPriceMax] = useState(bundle.total_price_max || 0);
  const [isPublished, setIsPublished] = useState(bundle.is_published);
  const [items, setItems] = useState<BundleItem[]>(bundle.items || []);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const handleSaveBundle = async () => {
    try {
      await bundlesData.updateBundle(bundle.id, {
        summary,
        total_price_min: priceMin,
        total_price_max: priceMax,
        is_published: isPublished,
      });

      toast({
        title: 'Bundle updated',
        description: 'Bundle information saved successfully',
      });
      onSave();
    } catch (error) {
      console.error('Error updating bundle:', error);
      toast({
        title: 'Error',
        description: 'Failed to update bundle',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateItem = async (itemId: string, updates: Partial<BundleItem>) => {
    try {
      await bundlesData.updateBundleItem(itemId, updates);
      setItems(prev => prev.map(item => item.id === itemId ? { ...item, ...updates } : item));
      toast({
        title: 'Item updated',
        description: 'Bundle item saved successfully',
      });
    } catch (error) {
      console.error('Error updating item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update item',
        variant: 'destructive',
      });
    }
  };

  const handleAddItem = async () => {
    try {
      const newItem = await bundlesData.addBundleItem(bundle.id, {
        name: 'New Item',
        description: 'Item description',
        search_query: 'search query',
        price_range: '$0-0',
        sort_order: items.length,
        estimated_price: 0,
        category: null,
        image_url: null,
      });
      setItems(prev => [...prev, newItem]);
      setExpandedItems(prev => new Set(prev).add(newItem.id));
      toast({
        title: 'Item added',
        description: 'New item created successfully',
      });
    } catch (error) {
      console.error('Error adding item:', error);
      toast({
        title: 'Error',
        description: 'Failed to add item',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await bundlesData.deleteBundleItem(itemId);
      setItems(prev => prev.filter(item => item.id !== itemId));
      toast({
        title: 'Item deleted',
        description: 'Bundle item removed successfully',
      });
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete item',
        variant: 'destructive',
      });
    }
  };

  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleImageUpload = async (itemId: string, file: File) => {
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${bundle.id}/${itemId}-${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('bundle-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('bundle-images')
        .getPublicUrl(fileName);

      // Update item with new image URL
      await handleUpdateItem(itemId, { image_url: publicUrl });

      toast({
        title: 'Image uploaded',
        description: 'Image uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Bundle: {bundle.slug}</DialogTitle>
          <DialogDescription>
            Update bundle information and manage items
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Bundle Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Bundle Information</h3>
            <div className="space-y-2">
              <Label htmlFor="summary">Summary</Label>
              <Textarea
                id="summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priceMin">Min Price ($)</Label>
                <Input
                  id="priceMin"
                  type="number"
                  value={priceMin}
                  onChange={(e) => setPriceMin(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="priceMax">Max Price ($)</Label>
                <Input
                  id="priceMax"
                  type="number"
                  value={priceMax}
                  onChange={(e) => setPriceMax(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="published"
                checked={isPublished}
                onCheckedChange={setIsPublished}
              />
              <Label htmlFor="published">Published</Label>
            </div>
          </div>

          <Separator />

          {/* Bundle Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Bundle Items ({items.length})</h3>
              <Button size="sm" onClick={handleAddItem}>
                <Plus className="mr-2 h-4 w-4" />
                Add Item
              </Button>
            </div>

            <div className="space-y-2">
              {items.map((item) => (
                <Collapsible
                  key={item.id}
                  open={expandedItems.has(item.id)}
                  onOpenChange={() => toggleItemExpanded(item.id)}
                >
                  <Card>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-left">{item.name}</span>
                            {item.category && (
                              <Badge variant="outline" className="text-xs">{item.category}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{item.price_range}</span>
                            {expandedItems.has(item.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 space-y-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={item.name}
                            onChange={(e) => handleUpdateItem(item.id, { name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={item.description}
                            onChange={(e) => handleUpdateItem(item.id, { description: e.target.value })}
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Search Query</Label>
                            <div className="flex gap-2">
                              <Input
                                value={item.search_query}
                                onChange={(e) => handleUpdateItem(item.id, { search_query: e.target.value })}
                              />
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={() => window.open(`https://www.amazon.com/s?k=${encodeURIComponent(item.search_query)}`, '_blank')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Price Range</Label>
                            <Input
                              value={item.price_range}
                              onChange={(e) => handleUpdateItem(item.id, { price_range: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Category</Label>
                            <Input
                              value={item.category || ''}
                              onChange={(e) => handleUpdateItem(item.id, { category: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Estimated Price ($)</Label>
                            <Input
                              type="number"
                              value={item.estimated_price || ''}
                              onChange={(e) => handleUpdateItem(item.id, { estimated_price: Number(e.target.value) })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Image</Label>
                          <div className="flex gap-4 items-start">
                            {item.image_url && (
                              <div className="shrink-0">
                                <img
                                  src={item.image_url}
                                  alt={item.name}
                                  className="w-20 h-20 object-cover rounded border"
                                />
                              </div>
                            )}
                            <div className="flex-1 space-y-2">
                              <div className="flex gap-2">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageUpload(item.id, file);
                                  }}
                                  className="flex-1"
                                />
                              </div>
                              <Input
                                placeholder="Or paste image URL"
                                value={item.image_url || ''}
                                onChange={(e) => handleUpdateItem(item.id, { image_url: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Item
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Item?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete "{item.name}". This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteItem(item.id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleSaveBundle}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBundles;
